import React, { useState, useEffect } from "react";
import { HiDotsHorizontal } from "react-icons/hi";
import DetailsConsultant from "../../../components/Modals/DetailsConsultant";
import DeleteClientModal from "../../../components/Modals/DeleteClientModal";
import ConsultantStatusModal from "../../Modals/ConsultantStatusModal";
import ViewModal from "../../Modals/ViewModal";
import axios from "axios";
import EditConsultantModal from "../../Modals/EditConsultantModal";

const ApprovedConsultants = ({ query, page, itemsPerPage, onDataChange }) => {
  const [modalIndex, setModalIndex] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [consultants, setConsultants] = useState([]);

  // Get Consultant - Filter by status 'approved'
  const fetchConsultant = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/consultants/get-consultants`
      );
      if (response?.data) {
        // Filter only approved consultants
        const approvedConsultants = response.data.filter(
          (consultant) => consultant.status === "approved"
        );
        setConsultants(approvedConsultants);
      } else {
        setError("No data received");
      }
    } catch (error) {
      setError("Failed to fetch consultants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultant();
  }, []);

  const filteredData = consultants.filter((item) =>
    item.consultantName?.toLowerCase().includes(query?.toLowerCase() || "")
  );

  // Pagination calculation
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  useEffect(() => {
    if (onDataChange) {
      onDataChange(totalPages, filteredData.length);
    }
  }, [filteredData.length, totalPages, onDataChange]);

  const handleIconClick = (index, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setModalPosition({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX - 50,
    });
    setModalIndex((prev) => (prev === index ? null : index));
  };

  const handleCloseModal = () => {
    setModalIndex(null);
  };

  const handleDeleteClick = (consultant) => {
    setSelectedConsultant(consultant);
    setShowDeleteModal(true);
    setModalIndex(null);
  };

  // Delete
  const handleConfirmDelete = async () => {
    if (!selectedConsultant?._id && !selectedConsultant?.id) {
      setError("No consultant ID found for deletion");
      return;
    }

    try {
      const idToDelete = selectedConsultant._id || selectedConsultant.id;
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/consultants/delete-consultant/${idToDelete}`
      );

      setShowDeleteModal(false);
      setSelectedConsultant(null);
      setSuccess("Consultant deleted successfully");
      fetchConsultant();
    } catch (err) {
      setError("Failed to delete consultant");
    }
  };

  const handleEditClick = (consultant) => {
    setSelectedConsultant(consultant);
    setShowEditModal(true);
    setModalIndex(null);
  };

  // Edit
  const handleEditSubmit = async (updatedData) => {
    try {
      const id = selectedConsultant._id || selectedConsultant.id;
      await axios.put(
        `${process.env.REACT_APP_API_URL}/consultants/edit-consultant/${id}`,
        updatedData,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      setShowEditModal(false);
      setSelectedConsultant(null);
      setSuccess("Consultant updated successfully");
      fetchConsultant();
    } catch (err) {
      setError("Failed to update consultant");
    }
  };

  const handleTransferClick = (consultant) => {
    setSelectedConsultant(consultant);
    setShowStatusModal(true);
    setModalIndex(null);
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (!selectedConsultant?._id && !selectedConsultant?.id) {
      setError("No consultant ID found for status change");
      return;
    }

    try {
      const id = selectedConsultant._id || selectedConsultant.id;
      await axios.put(
        `${process.env.REACT_APP_API_URL}/consultants/change-consultant-status/${id}`,
        { status: newStatus },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      setShowStatusModal(false);
      setSelectedConsultant(null);
      setSuccess(`Consultant status changed to ${newStatus} successfully`);
      fetchConsultant();
    } catch (err) {
      setError("Failed to change consultant status");
    }
  };

  const handleViewClick = (consultant) => {
    setSelectedConsultant(consultant);
    setShowViewModal(true);
    setModalIndex(null);
  };

  return (
    <div className="px-2 mb-6 relative">
      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-4 text-blue-600">
          Loading consultants...
        </div>
      ) : paginatedClients.length === 0 ? (
        <div className="flex justify-center">
          <p className="p-4 text-gray-500">No approved consultants found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full min-w-[1300px] divide-y divide-gray-200">
            <thead className="bg-[#F1F7FF]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider rounded-tl-lg">
                  Sl.
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider">
                  C Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider">
                  Company Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider">
                  Aadhar Number
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider">
                  Company Reg Proof
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#646B72] uppercase tracking-wider rounded-tr-lg">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedClients.map((item, index) => (
                <tr
                  key={item._id ?? item.id ?? index}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 text-sm text-[#333448]">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-[#333448]">
                    {item.consultantName}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#333448]">
                    {item.phone}
                  </td>
                  <td
                    className="px-6 py-4 text-sm text-[#333448] truncate max-w-[150px]"
                    title={item.email}
                  >
                    {item.email}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#333448]">
                    {item.companyName}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#333448]">
                    {item.country || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#333448]">
                    {item.aadharCard || item.aadharNumber || "0000 0000 0000"}
                  </td>
                  <td className="px-6 py-4">
                    {item.companyRegistrationProof ? (
                      <img
                        src={item.companyRegistrationProof}
                        alt="Company Registration"
                        className="w-16 h-16 object-cover rounded border shadow-sm"
                      />
                    ) : (
                      <span className="text-gray-500 text-xs italic">
                        No image
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#333448]">
                    <button
                      onClick={(e) => handleIconClick(index, e)}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                    >
                      <HiDotsHorizontal size={18} />
                    </button>
                    {modalIndex === index && (
                      <DetailsConsultant
                        onClose={handleCloseModal}
                        onDeleteClick={() => handleDeleteClick(item)}
                        onEditClick={() => handleEditClick(item)}
                        onTransferClick={() => handleTransferClick(item)}
                        onViewClick={() => handleViewClick(item)}
                        position={modalPosition}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <DeleteClientModal
          itemName={selectedConsultant?.consultantName}
          onConfirm={handleConfirmDelete}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditConsultantModal
          mode="edit"
          initialData={selectedConsultant}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEditSubmit}
        />
      )}

      {/* Consultant Status Modal */}
      {showStatusModal && (
        <ConsultantStatusModal
          consultant={selectedConsultant}
          onClose={() => setShowStatusModal(false)}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* View Modal */}
      {showViewModal && (
        <ViewModal
          consultant={selectedConsultant}
          onClose={() => {
            setShowViewModal(false);
            setSelectedConsultant(null);
          }}
        />
      )}
    </div>
  );
};

export default ApprovedConsultants;
